// NOTE: This script is loaded with `async` (not `defer`) so its execution isn't
// blocked by document-order-dependent scripts (e.g. slow-executing 3rd-party scripts
// in <head>, which otherwise delay this file from running).
// Because `async` scripts can execute before the HTML parser has reached
// <hls-video-player> elements further down the page, we must NOT bail out early
// based on a synchronous DOM query here (that previously worked only because
// `defer` guaranteed the whole document was parsed first).
// `customElements.define` below is safe to always call: the browser will
// automatically "upgrade" any matching elements already in the DOM as well as
// any added later, regardless of when this script executes.
if (!customElements.get('hls-video-player')) {
  // Last-resort timeout to reveal the video if neither requestVideoFrameCallback
  // nor the 'playing' event fires, so the poster never stays stuck.
  const REVEAL_FALLBACK_TIMEOUT_MS = 3000;

  class HLSVideoPlayer extends HTMLElement {
    static hlsLibLoaded = false;
    static hlsLoadPromise = null;

    constructor() {
      super();
      this.isPlaying = false;
      this.isVideoReady = false;
      this.hlsInstance = null;
      this.video = null;
      this.poster = null;
      this.wrapper = null;
      this.loadingSpinner = null;
      this.pendingAutoplay = null;
      this.pendingTime = null;
      this.usingNativeHLS = false;
      this.audioButtons = [];
      this.audioToggle = null;
      this.controlsId = '';
      this.activeAudioOption = 'primary';
      this.isMinimalPlayer = false;
      this.isLoading = false;
      this.interactiveLoad = false;
      this.playbackInitiated = false;
      this.mediaShell = null;
      this.listenersAttached = false;
      this.freezeOverlay = null;
    }

    connectedCallback() {
      // `<responsive-order>` (assets/global.js) re-appends its children on
      // every breakpoint change, even when their order hasn't changed.
      // `appendChild` on an already-attached node is a synchronous
      // remove-then-insert, which fires disconnectedCallback() immediately
      // followed by connectedCallback() again on this element — a DOM
      // *move*, not a real teardown. If a destroy() from that disconnect is
      // still pending (see disconnectedCallback below), cancel it and skip
      // re-initialization: existing playback state / hlsInstance are still
      // valid and untouched. Without this guard, every reorder would kill a
      // working HLS.js instance and spin up a second, concurrent one via
      // tryAutoplay(), leaving `isLoading` stuck and the play/pause toggle
      // permanently unresponsive.
      if (this._pendingDestroy) {
        this._pendingDestroy = false;
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      this._childObserver?.disconnect();
      this._childObserver = null;

      // Defer destroy() by a microtask so a same-task reconnect (a DOM move,
      // not a real removal) can cancel it in connectedCallback() above.
      this._pendingDestroy = true;
      queueMicrotask(() => {
        if (this._pendingDestroy) {
          this._pendingDestroy = false;
          this.destroy();
        }
      });
    }

    static loadHLSLibrary() {
      if (HLSVideoPlayer.hlsLoadPromise) {
        return HLSVideoPlayer.hlsLoadPromise;
      }

      HLSVideoPlayer.hlsLoadPromise = new Promise((resolve, reject) => {
        if (HLSVideoPlayer.hlsLibLoaded) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/hls.js@1';
        script.onload = () => {
          HLSVideoPlayer.hlsLibLoaded = true;
          resolve();
        };
        script.onerror = () => reject(new Error('Failed to load HLS.js'));
        document.head.append(script);
      });

      return HLSVideoPlayer.hlsLoadPromise;
    }

    init() {
      // Under `async` loading, this element can be upgraded (connectedCallback
      // fired) before the HTML parser has finished appending its own children
      // (see comment at top of file). If the expected children aren't present
      // yet, wait for them via MutationObserver instead of silently bailing out
      // forever.
      const poster = this.querySelector('.hls-poster[data-hls-url]');
      const video = this.querySelector('.hls-video-player');

      if (poster && video) {
        this.initWithChildren(poster, video);
        return;
      }

      if (this._childObserver) return;

      this._childObserver = new MutationObserver(() => {
        const nextPoster = this.querySelector('.hls-poster[data-hls-url]');
        const nextVideo = this.querySelector('.hls-video-player');

        if (nextPoster && nextVideo) {
          this._childObserver.disconnect();
          this._childObserver = null;
          this.initWithChildren(nextPoster, nextVideo);
        }
      });
      this._childObserver.observe(this, { childList: true, subtree: true });
    }

    initWithChildren(poster, video) {
      this.poster = poster;
      this.wrapper = this;
      this.video = video;
      this.loadingSpinner = this.querySelector('.hls-loading-spinner');

      this.isMinimalPlayer = this.dataset.playerStyle === 'minimal';
      this.mediaShell = this.closest('.video-section__media-shell');

      this.defaultHlsUrl = this.dataset.defaultHls || this.poster.dataset.hlsUrl;
      this.audioDescriptionHlsUrl = this.dataset.audioDescriptionHls || '';
      this.hlsUrl =
        this.audioDescriptionHlsUrl && this.dataset.initialAudio === 'description'
          ? this.audioDescriptionHlsUrl
          : this.defaultHlsUrl;
      this.poster.dataset.hlsUrl = this.hlsUrl;
      this.autoplay = this.video.hasAttribute('autoplay');
      this.loop = this.video.hasAttribute('loop');

      if (this.isMinimalPlayer) {
        this.video.removeAttribute('controls');
        this.video.muted = true;
      }

      this.setupAccessibility();
      this.setupEventListeners();
      this.setupAudioToggle();
      this.setupMuteToggle();
      this.initializeHLSVideo();

      if (this.autoplay && !this.prefersReducedMotion()) {
        this.tryAutoplay();
      }
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    initializeHLSVideo() {
      customElements.whenDefined('hls-video-player').then(() => {
        this.classList.remove('hls-loading');
        this.classList.add('hls-ready');

        if (this.prefersReducedMotion()) return;

        this.poster.removeAttribute('disabled');

        // Update accessibility attributes
        this.poster.setAttribute('role', 'button');
        this.poster.setAttribute('aria-controls', this.video.id);
        this.updatePosterAccessibility();

        // Announce to screen readers that video is ready
        const srAnnouncement = document.createElement('div');
        srAnnouncement.setAttribute('aria-live', 'polite');
        srAnnouncement.setAttribute('aria-atomic', 'true');
        srAnnouncement.className = 'visually-hidden';
        srAnnouncement.textContent = 'Video player is ready. Press space or enter to play.';
        this.append(srAnnouncement);

        // Remove announcement after screen readers have had time to read it
        setTimeout(() => {
          if (srAnnouncement.parentNode) {
            srAnnouncement.parentNode.removeChild(srAnnouncement);
          }
        }, 3000);
      });
    }

    setupAccessibility() {
      const ariaInitial = this.video.dataset.ariaInitial || 'Video player - press space or enter to start playback';
      const ariaPlay = this.video.dataset.ariaPlay || 'Video player - press space or enter to play';
      const ariaPause = this.video.dataset.ariaPause || 'Video player - press space or enter to pause';
      const minimalPlayerAlt = this.poster?.dataset.alt?.trim();

      this.ariaLabels = {
        initial: ariaInitial,
        play: this.isMinimalPlayer ? this.buildMinimalPlayerLabel('play', minimalPlayerAlt) : ariaPlay,
        pause: this.isMinimalPlayer ? this.buildMinimalPlayerLabel('pause', minimalPlayerAlt) : ariaPause,
      };

      this.updateVideoAccessibility(true);
    }

    buildMinimalPlayerLabel(state, altText) {
      const action =
        state === 'pause' ? this.dataset.pauseLabel || 'Pause video' : this.dataset.playLabel || 'Play video';

      if (!altText) {
        return action;
      }

      return `${action}: ${altText}`;
    }

    usesPosterAsPrimaryControl() {
      return this.isMinimalPlayer;
    }

    shouldLabelVideo() {
      return !this.usesPosterAsPrimaryControl();
    }

    shouldTransferFocusToVideo() {
      return !this.usesPosterAsPrimaryControl();
    }

    updateVideoAccessibility(isInitialState = false) {
      if (!this.shouldLabelVideo()) {
        return;
      }

      if (isInitialState) {
        this.video.setAttribute('aria-label', this.ariaLabels.initial);
        return;
      }

      if (!this.isVideoReady) {
        return;
      }

      const label = this.video.paused ? this.ariaLabels.play : this.ariaLabels.pause;
      this.video.setAttribute('aria-label', label);
    }

    updatePosterAccessibility() {
      if (!this.poster || !this.video) {
        return;
      }

      const isPlaying =
        this.isLoading && this.interactiveLoad
          ? this.pendingAutoplay !== false
          : this.isVideoReady && !this.video.paused && !this.video.ended;
      const label = isPlaying ? this.ariaLabels.pause : this.ariaLabels.play;

      this.poster.setAttribute('aria-label', label);
      this.poster.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    }

    setupEventListeners() {
      if (this.listenersAttached) return;
      this.listenersAttached = true;

      this.video.addEventListener('play', () => this.updatePlayingState());
      this.video.addEventListener('pause', () => this.updatePlayingState());
      this.video.addEventListener('playing', () => this.updatePlayingState());
      this.video.addEventListener('waiting', () => this.updatePlayingState());
      this.video.addEventListener('ended', () => this.handleVideoEnded());
      this.video.addEventListener('loadedmetadata', () => this.updatePortraitClass());
      this.video.addEventListener('keydown', (e) => this.handleKeydown(e));

      this.poster.addEventListener('click', (e) => this.handlePosterClick(e));

      this.setupMobileHandling();
    }

    setupMuteToggle() {
      this.muteToggle = this.querySelector('[data-mute-toggle]');
      if (!this.muteToggle || !this.isMinimalPlayer) return;

      this.updateMuteToggleState();

      this.muteToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.video.muted = !this.video.muted;
        this.updateMuteToggleState();
      });

      this.video.addEventListener('volumechange', () => this.updateMuteToggleState());
    }

    updateMuteToggleState() {
      if (!this.muteToggle || !this.video) return;

      const isMuted = this.video.muted;
      this.muteToggle.setAttribute('aria-pressed', isMuted ? 'false' : 'true');
      this.muteToggle.setAttribute(
        'aria-label',
        isMuted
          ? this.muteToggle.dataset.unmuteLabel || 'Unmute video'
          : this.muteToggle.dataset.muteLabel || 'Mute video'
      );
      this.muteToggle.classList.toggle('is-muted', isMuted);
    }

    setupAudioToggle() {
      this.controlsId = this.dataset.controlsId || '';
      if (!this.controlsId) {
        return;
      }

      this.audioToggle = document.querySelector(`[data-audio-toggle-for="${this.controlsId}"]`);
      if (!this.audioToggle) return;

      this.audioButtons = Array.from(this.audioToggle.querySelectorAll('[data-audio-option]'));
      if (this.audioButtons.length === 0) return;

      const initialOption =
        this.hlsUrl === this.audioDescriptionHlsUrl && this.audioDescriptionHlsUrl ? 'description' : 'primary';
      this.activeAudioOption = initialOption;
      this.updateAudioToggleState(initialOption, { announce: false });

      this.audioButtons.forEach((button) => {
        button.hidden = button.dataset.audioOption === this.activeAudioOption;
        button.addEventListener('click', () => {
          const option = button.dataset.audioOption;
          if (!option) return;

          const desiredOption = option === this.activeAudioOption ? this.getAlternateAudioOption(option) : option;
          this.switchAudioOption(desiredOption);
        });

        button.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const targetOption = this.getAlternateAudioOption(this.activeAudioOption);
          this.switchAudioOption(targetOption);
        });
      });
    }

    setupMobileHandling() {
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        'ontouchstart' in window;

      if (this.usesPosterAsPrimaryControl()) {
        return;
      }

      if (isMobile) {
        this.video.removeAttribute('controls');
        this.video.setAttribute('tabindex', '0');

        // Create transparent overlay for touch handling
        const touchOverlay = document.createElement('div');
        touchOverlay.className = 'hls-touch-overlay';
        this.wrapper.append(touchOverlay);

        touchOverlay.addEventListener('touchend', (e) => this.handlePlayToggle(e));
        touchOverlay.addEventListener('click', (e) => this.handlePlayToggle(e));
        this.video.addEventListener('touchend', (e) => this.handlePlayToggle(e));
        this.video.addEventListener('click', (e) => this.handlePlayToggle(e));
      } else {
        this.video.setAttribute('tabindex', '0');
        this.video.addEventListener('click', (e) => this.handlePlayToggle(e));
      }
    }

    handleVideoEnded() {
      this.updatePlayingState();
      if (!this.loop && this.video.currentTime >= this.video.duration - 0.5) {
        this.hideVideo();
      }
    }

    getAlternateAudioOption(option) {
      return option === 'description' ? 'primary' : 'description';
    }

    switchAudioOption(option) {
      if (!option || option === this.activeAudioOption) {
        return;
      }

      const targetButton = this.audioButtons.find((button) => button.dataset.audioOption === option);
      if (!targetButton) {
        return;
      }

      const newUrl = targetButton.dataset.hlsUrl;
      if (!newUrl) {
        return;
      }

      const wasPlaying = this.isVideoReady && !this.video.paused && !this.video.ended;
      const resumeTime = this.isVideoReady ? this.video.currentTime : 0;

      this.activeAudioOption = option;
      this.updateAudioToggleState(option);
      requestAnimationFrame(() => {
        const nextButton = this.audioButtons.find((button) => !button.hidden);
        nextButton?.focus();
      });
      this.poster.dataset.hlsUrl = newUrl;
      this.hlsUrl = newUrl;

      if (!this.isVideoReady) {
        return;
      }

      this.pendingAutoplay = wasPlaying;
      this.pendingTime = resumeTime;
      this.poster.setAttribute('disabled', '');
      this.hideVideo();
      this.startPlayback();
    }

    updateAudioToggleState(option, { announce = true } = {}) {
      if (!this.audioButtons?.length) return;

      this.audioButtons.forEach((button) => {
        const isActive = button.dataset.audioOption === option;
        button.hidden = isActive;
        button.setAttribute('aria-hidden', isActive ? 'true' : 'false');
        button.removeAttribute('aria-live');
        delete button.dataset.announced;
        if (!isActive && announce) {
          button.setAttribute('aria-live', 'polite');
          button.dataset.announced = 'true';
        }
      });
    }

    clampResumeTime(desiredTime) {
      // Playback of both videos is synchronized. When the video source is switched, the playback position of the other player gets adjusted accordingly. This function ensures the time value is kept within safe bounds
      if (typeof desiredTime !== 'number' || Number.isNaN(desiredTime) || desiredTime <= 0) {
        return 0;
      }

      const duration = this.video?.duration;

      if (typeof duration === 'number' && !Number.isNaN(duration) && duration > 0) {
        const epsilon = 0.4;
        // Using this tolerance check prevents floating-point precision issues;
        // Or starting at the very end and immediately triggering "ended" event;
        // Or edge cases where currentTime might be slightly past duration
        if (desiredTime >= duration - epsilon) {
          return 0;
        }
      }

      return desiredTime;
    }

    applyResumeTime(desiredTime) {
      const safeTime = this.clampResumeTime(desiredTime);

      if (!this.video) {
        return 0;
      }

      try {
        this.video.currentTime = safeTime;
        return safeTime;
      } catch {
        this.video.currentTime = 0;
        return 0;
      }
    }

    handlePosterClick(e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.usesPosterAsPrimaryControl() && this.isVideoReady) {
        this.togglePlayback();
        return;
      }

      if (this.isLoading && this.interactiveLoad) {
        if (this.playbackInitiated) {
          const wantPlay = this.video.paused;
          this.pendingAutoplay = wantPlay;
          if (wantPlay) {
            this.video.play().catch(() => {});
          } else {
            this.video.pause();
          }
        } else {
          this.pendingAutoplay = !(this.pendingAutoplay === true);
        }
        this.updatePosterAccessibility();
        return;
      }

      if (!this.isVideoReady && !this.isLoading) {
        this.pendingAutoplay = true;
        this.startPlayback();
      }
    }

    handlePlayToggle(e) {
      e.preventDefault();
      e.stopPropagation();
      if (this.isLoading && this.interactiveLoad) {
        if (this.playbackInitiated) {
          const wantPlay = this.video.paused;
          this.pendingAutoplay = wantPlay;
          if (wantPlay) {
            this.video.play().catch(() => {});
          } else {
            this.video.pause();
          }
        } else {
          this.pendingAutoplay = !(this.pendingAutoplay === true);
        }
        this.updatePosterAccessibility();
        return;
      }
      if (!this.isVideoReady) {
        this.pendingAutoplay = true;
        this.startPlayback();
      } else {
        this.togglePlayback();
      }
    }

    handleKeydown(e) {
      if (e.code === 'Enter' && this.isVideoReady) {
        e.preventDefault();
        e.stopPropagation();
        this.togglePlayback();
      }
    }

    showLoading() {
      this.loadingSpinner?.classList.add('hls-loading-spinner--visible');
      this.wrapper?.classList.add('hls-buffering');
      this.poster?.setAttribute('aria-busy', 'true');
    }

    hideLoading() {
      this.loadingSpinner?.classList.remove('hls-loading-spinner--visible');
      this.wrapper?.classList.remove('hls-buffering');
      this.poster?.removeAttribute('aria-busy');
    }

    revealVideoWhenPlaying() {
      this.video.classList.remove('hls-video-player--hidden');

      let revealed = false;
      const reveal = () => {
        if (revealed) return;
        revealed = true;
        this.showVideo();
      };

      if (typeof this.video.requestVideoFrameCallback === 'function') {
        this.video.requestVideoFrameCallback(() => reveal());
      } else {
        this.video.addEventListener('playing', () => reveal(), { once: true });
      }

      setTimeout(reveal, REVEAL_FALLBACK_TIMEOUT_MS);
    }

    finishPlaybackSetup(desiredResumeTime) {
      const shouldAutoplay = this.pendingAutoplay === true || this.autoplay;
      this.pendingTime = null;
      this.pendingAutoplay = null;

      this.applyResumeTime(desiredResumeTime);

      if (shouldAutoplay) {
        this.playbackInitiated = true;
        this.revealVideoWhenPlaying();
        const playPromise = this.video.play();
        if (playPromise && typeof playPromise.finally === 'function') {
          playPromise.finally(() => this.scheduleFreezeOverlayRemoval());
        } else {
          this.scheduleFreezeOverlayRemoval();
        }
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => this.showVideo());
        }
      } else {
        this.showVideo();
        this.video.pause();
        this.updatePlayingState();
        this.scheduleFreezeOverlayRemoval();
      }
    }

    showVideo() {
      this.isVideoReady = true;
      this.isLoading = false;
      this.wrapper.classList.add('playing');
      this.mediaShell?.classList.add('video-section__media-shell--playing');
      this.video.classList.remove('hls-video-player--hidden');
      this.poster?.removeAttribute('disabled');
      this.poster?.removeAttribute('hidden');
      this.hideLoading();
      this.updatePlayingState();

      // Transfer focus if poster was just activated
      if (document.activeElement === this.poster && this.shouldTransferFocusToVideo()) {
        setTimeout(() => {
          this.video.focus();
        }, 100);
      }
    }

    updatePortraitClass() {
      // Tag 9:16 portrait videos so the gallery letterboxes them instead of cropping.
      if (!this.wrapper || !this.wrapper.classList.contains('product__media-video')) return;
      const w = this.video?.videoWidth;
      const h = this.video?.videoHeight;
      if (!w || !h) return;
      this.wrapper.classList.toggle('hls-video-wrapper--portrait', w / h <= 0.563);
    }

    hideVideo() {
      this.isPlaying = false;
      this.isVideoReady = false;
      this.isLoading = false;
      this.playbackInitiated = false;
      this.hideLoading();
      this.wrapper.classList.remove('playing');
      this.mediaShell?.classList.remove('video-section__media-shell--playing');
      this.video.classList.add('hls-video-player--hidden');
      this.video.pause();
      this.updatePosterAccessibility();
      this.removeFreezeOverlay();

      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
    }

    updatePlayingState() {
      this.isPlaying = !this.video.paused;

      this.updateVideoAccessibility();

      this.updatePosterAccessibility();
    }

    togglePlayback() {
      if (!this.isVideoReady) {
        this.startPlayback();
        return;
      }

      if (this.video.paused) {
        this.video.play().catch((error) => {
          console.error('Play failed:', error);
        });
      } else {
        this.video.pause();
      }
    }

    loadContent() {
      if (!this.video || !this.poster) return;

      const wantsAutoplay = this.dataset.galleryAutoplay === 'true';
      const reducedMotion =
        typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!wantsAutoplay || reducedMotion) return;

      if (this.isVideoReady) {
        if (this.video.paused) {
          this.video.play().catch(() => {});
        }
        return;
      }

      if (this.isLoading) return;

      this.pendingAutoplay = true;
      this.startPlayback(false);
    }

    tryAutoplay(attempt = 1) {
      if (attempt > 100) {
        return;
      }

      if (this.isPlaying || this.isVideoReady) {
        return;
      }

      setTimeout(() => {
        if (!this.isPlaying && !this.isVideoReady) {
          this.startPlayback(false);
          this.tryAutoplay(attempt + 1);
        }
      }, 100);
    }

    startPlayback(userInitiated = true) {
      if (this.isPlaying || this.isLoading) return;

      this.interactiveLoad = userInitiated;
      this.playbackInitiated = false;
      if (userInitiated) {
        this.updatePosterAccessibility();
      }

      const hasNativeHLS = Boolean(this.video.canPlayType('application/vnd.apple.mpegurl'));
      // The inline early-play script (see video-player-hls.liquid) may have
      // already unhidden and started native HLS playback before this module
      // finished loading. Adopt that state instead of calling setupNativeHLS(),
      // which would reset `src`/call `.load()` and cause a visible restart.
      const alreadyPlayingNatively =
        hasNativeHLS &&
        this.video.dataset.nativeEarlyPlay === 'true' &&
        !this.video.classList.contains('hls-video-player--hidden');

      if (alreadyPlayingNatively) {
        this.usingNativeHLS = true;
        this.showVideo();
      } else {
        this.isLoading = true;
        this.poster?.setAttribute('disabled', '');
        this.showLoading();

        // If native HLS is supported, start playback immediately without waiting for the HLS.js lib.
        if (hasNativeHLS) {
          this.setupNativeHLS();
        }
      }

      // Always load HLS.js in parallel. Once ready, prefer it over native HLS.
      HLSVideoPlayer.loadHLSLibrary()
        .then(() => {
          if (typeof Hls === 'undefined' || !Hls.isSupported()) {
            // HLS.js unavailable — native HLS handles playback, or error if neither is available.
            if (!hasNativeHLS) {
              console.error('HLS not supported');
              this.isLoading = false;
              this.hideLoading();
              this.poster?.removeAttribute('disabled');
            }
            return;
          }

          // HLS.js is ready. If native was already running, capture current state before switching.
          if (this.usingNativeHLS) {
            this.pendingTime = this.isVideoReady ? this.video.currentTime : 0;
            if (this.isVideoReady) {
              // Native HLS was playing/paused — capture that state for HLS.js to resume.
              this.pendingAutoplay = !this.video.paused;
              // Snapshot the current frame and overlay it before pausing/detaching
              // the native player, so the viewer sees a frozen frame instead of a
              // pause -> poster/black -> resume flash during the engine handoff.
              this.freezeOverlay = this.createFreezeOverlay();
            }
            // isVideoReady=false means native loadedmetadata hasn't fired yet.
            // pendingAutoplay is preserved as-is (the user's original play intent).
            this.video.pause();
            this.usingNativeHLS = false;
            this.isVideoReady = false;
          }

          this.setupHLSJS();
        })
        .catch(() => {
          if (!hasNativeHLS) {
            console.error('HLS playback failed');
            this.isLoading = false;
            this.hideLoading();
            this.poster?.removeAttribute('disabled');
          }
        });
    }

    setupNativeHLS() {
      this.usingNativeHLS = true;
      const desiredResumeTime = typeof this.pendingTime === 'number' ? this.pendingTime : 0;
      this.pendingTime = null;
      // pendingAutoplay is intentionally NOT read or consumed here.
      // Reading it synchronously would race against the HLS.js CDN load:
      // if HLS.js arrives before loadedmetadata fires, the takeover code overwrites
      // pendingAutoplay and the user's play intent (set in handlePosterClick) is lost.
      // Instead, we read it inside the loadedmetadata handler where the race is resolved.

      this.video.src = this.hlsUrl;
      this.video.loop = this.loop;
      this.video.load();

      this.video.addEventListener(
        'loadedmetadata',
        () => {
          // If HLS.js has taken over since native setup started, skip.
          if (!this.usingNativeHLS) return;

          this.finishPlaybackSetup(desiredResumeTime);
        },
        { once: true }
      );
    }

    setupHLSJS() {
      this.usingNativeHLS = false;
      const hls = new Hls({
        startLevel: -1,
        capLevelToPlayerSize: true,
        maxBufferLength: 10,
        maxMaxBufferLength: 30,
        lowLatencyMode: false,
        backBufferLength: 10,
        abrEwmaFastLive: 2.0,
        abrEwmaSlowLive: 5.0,
        abrEwmaFastVoD: 2.0,
        abrEwmaSlowVoD: 5.0,
        abrMaxWithRealBitrate: false,
        debug: false,
      });

      hls.loadSource(this.hlsUrl);
      hls.attachMedia(this.video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.video.loop = this.loop;

        const desiredResumeTime = typeof this.pendingTime === 'number' ? this.pendingTime : 0;
        this.finishPlaybackSetup(desiredResumeTime);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error('Fatal HLS error');
          this.isLoading = false;
          this.hideLoading();
          this.poster?.removeAttribute('disabled');
          this.removeFreezeOverlay();
        }
      });

      this.hlsInstance = hls;
      this.video.hlsInstance = hls;
    }

    // Snapshot the currently visible video frame into a canvas overlaid on top
    // of the video, so an engine handoff (native HLS -> HLS.js) reads as a
    // brief stutter on a still frame rather than a pause/poster/black flash.
    createFreezeOverlay() {
      if (!this.video || !this.video.videoWidth || !this.video.videoHeight) {
        return null;
      }

      this.removeFreezeOverlay();

      const canvas = document.createElement('canvas');
      canvas.width = this.video.videoWidth;
      canvas.height = this.video.videoHeight;
      canvas.className = 'hls-freeze-overlay';
      canvas.setAttribute('aria-hidden', 'true');
      canvas.style.cssText =
        'position:absolute; inset:0; width:100%; height:100%; z-index:2; pointer-events:none; object-fit:cover;';

      try {
        canvas.getContext('2d').drawImage(this.video, 0, 0, canvas.width, canvas.height);
      } catch {
        // Drawing can throw (e.g. tainted canvas); skip the overlay rather than block the handoff.
        return null;
      }

      const container = this.video.parentElement || this.wrapper;
      if (!container) {
        return null;
      }

      const containerPosition = getComputedStyle(container).position;
      if (containerPosition === 'static') {
        container.style.position = 'relative';
      }

      container.append(canvas);
      return canvas;
    }

    removeFreezeOverlay() {
      this.freezeOverlay?.remove();
      this.freezeOverlay = null;
    }

    // Wait for an actual painted HLS.js frame before removing the freeze
    // overlay, so the swap underneath it is never visible.
    scheduleFreezeOverlayRemoval() {
      if (!this.freezeOverlay) {
        return;
      }

      const overlay = this.freezeOverlay;
      this.freezeOverlay = null;
      let removed = false;
      const remove = () => {
        if (removed) return;
        removed = true;
        overlay.remove();
      };

      if (typeof this.video.requestVideoFrameCallback === 'function') {
        this.video.requestVideoFrameCallback(remove);
      } else {
        this.video.addEventListener('playing', remove, { once: true });
      }

      // Safety net in case the video never fires a frame/playing event
      // (e.g. autoplay was blocked), so the overlay doesn't linger forever.
      setTimeout(remove, 500);
    }

    destroy() {
      this.mediaShell?.classList.remove('video-section__media-shell--playing');
      this.removeFreezeOverlay();

      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }

      this.isVideoReady = false;
      this.isPlaying = false;
      this.isLoading = false;
      this.playbackInitiated = false;
      this.pendingAutoplay = null;
      this.usingNativeHLS = false;
      this.wrapper?.classList.remove('playing');
      this.video?.classList.add('hls-video-player--hidden');
    }
  }

  customElements.define('hls-video-player', HLSVideoPlayer);
}
